/**
 * Documents légaux — Phase 9 (RGPD opérationnel, suite).
 *
 * Remplace les placeholders « Bientôt disponible » des trois liens du
 * footer Réglages (CGU, Politique de confidentialité, Règlement du
 * programme) par une modale scrollable (`ui/Modal.ts`).
 *
 * Le contenu vit intégralement dans les dictionnaires i18n
 * (`legal_terms_*`, `legal_privacy_*`, `legal_rules_*`) — 7 langues,
 * garanti par `tests/unit/i18n-parity.test.ts`. Chaque document est
 * une liste de sections (titre + corps) ; les corps contiennent des
 * paragraphes séparés par `\n\n`, éclatés en `<p>` au rendu.
 *
 * ${Icon(Icons.alert, 14)} Rédaction provisoire (version alpha) : chaque document affiche le
 * bandeau `legal_draft_notice` — à faire relire par un juriste avant
 * toute publication publique.
 */
import { html, type TemplateResult } from 'lit-html';
import styles from './View.module.css';
import { t, type TKey } from '../../i18n/index.js';
import { closeModal, getModal, openModal } from '../../stores/ui.js';
import { modalTemplate } from '../../ui/Modal.js';

const MODAL_LEGAL_PREFIX = 'settings:legal:';

export type LegalDocId = 'terms' | 'privacy' | 'rules';

interface LegalSection {
  title: TKey;
  body: TKey;
}

interface LegalDoc {
  /** Clé i18n du titre de la modale (réutilise les libellés du footer). */
  title: TKey;
  sections: readonly LegalSection[];
}

export const LEGAL_DOCS: Readonly<Record<LegalDocId, LegalDoc>> = {
  terms: {
    title: 'terms',
    sections: [
      { title: 'legal_terms_s1_title', body: 'legal_terms_s1_body' },
      { title: 'legal_terms_s2_title', body: 'legal_terms_s2_body' },
      { title: 'legal_terms_s3_title', body: 'legal_terms_s3_body' },
      { title: 'legal_terms_s4_title', body: 'legal_terms_s4_body' },
      { title: 'legal_terms_s5_title', body: 'legal_terms_s5_body' },
      { title: 'legal_terms_s6_title', body: 'legal_terms_s6_body' },
    ],
  },
  privacy: {
    title: 'privacy',
    sections: [
      { title: 'legal_privacy_s1_title', body: 'legal_privacy_s1_body' },
      { title: 'legal_privacy_s2_title', body: 'legal_privacy_s2_body' },
      { title: 'legal_privacy_s3_title', body: 'legal_privacy_s3_body' },
      { title: 'legal_privacy_s4_title', body: 'legal_privacy_s4_body' },
      { title: 'legal_privacy_s5_title', body: 'legal_privacy_s5_body' },
      { title: 'legal_privacy_s6_title', body: 'legal_privacy_s6_body' },
    ],
  },
  rules: {
    title: 'rules',
    sections: [
      { title: 'legal_rules_s1_title', body: 'legal_rules_s1_body' },
      { title: 'legal_rules_s2_title', body: 'legal_rules_s2_body' },
      { title: 'legal_rules_s3_title', body: 'legal_rules_s3_body' },
      { title: 'legal_rules_s4_title', body: 'legal_rules_s4_body' },
      { title: 'legal_rules_s5_title', body: 'legal_rules_s5_body' },
    ],
  },
};

export function openLegalModal(doc: LegalDocId): void {
  openModal(MODAL_LEGAL_PREFIX + doc);
}

/** Corps de section : paragraphes séparés par `\n\n` → un `<p>` chacun. */
function paragraphs(body: TKey): TemplateResult[] {
  return t(body)
    .split('\n\n')
    .map((p) => html`<p>${p}</p>`);
}

function legalDocTemplate(doc: LegalDoc): TemplateResult {
  return html`
    <article class=${styles.legalDoc}>
      <p class=${styles.legalDraftNotice} role="note" data-testid="legal-draft-notice">
        ${t('legal_draft_notice')}
      </p>
      <p class=${styles.legalVersionLine}>${t('legal_version_line')}</p>
      ${doc.sections.map(
        (s) => html`
          <h3>${t(s.title)}</h3>
          ${paragraphs(s.body)}
        `,
      )}
    </article>
  `;
}

/**
 * Modale des documents légaux — à rendre depuis `View.ts` au niveau
 * racine de la section (même câblage que login/register).
 */
export function legalModalTemplate(): TemplateResult | null {
  const modal = getModal();
  if (modal === null || !modal.startsWith(MODAL_LEGAL_PREFIX)) return null;
  const docId = modal.slice(MODAL_LEGAL_PREFIX.length) as LegalDocId;
  const doc = LEGAL_DOCS[docId];
  if (!doc) return null;
  return modalTemplate({
    title: t(doc.title),
    content: legalDocTemplate(doc),
    onClose: (): void => {
      closeModal();
    },
    closeLabel: t('close'),
    testid: `modal-legal-${docId}`,
  });
}
